import { get } from 'svelte/store';
import { queryFilters } from '$lib/stores/selection';
import { selectedIndustryCentroids } from '$lib/stores/data';
import { getRoleLabel } from '$lib/utils/roleMapping';

/** One named group of the current selection, ready to be read aloud. */
export interface FilterLine {
  label: string;
  value: string;
}

type Translate = (key: string, options?: { default?: string }) => string;

/**
 * What the customer has selected, in words.
 *
 * # Why this is not the filter bar's list
 *
 * The bar builds one removable chip per value — a canton each, a postal code
 * each — because each one has to be dismissable on its own. Read as a sentence
 * that is unbearable ("Kanton: ZH, Kanton: BE, Kanton: AG"), so this groups by
 * field instead and joins the values. The two lists answer the same question in
 * shapes that cannot be shared: one is a set of buttons, this is prose.
 *
 * # Why it exists at all
 *
 * The assistant is now handed the filters the customer arrived with, and being
 * handed them silently is worse than not having them: the customer cannot tell
 * whether the thing they are about to refine is the selection on screen or a
 * blank slate. So it says what it received, and this is what it says.
 */
export function describeSelection(t: Translate): FilterLine[] {
  const filters = get(queryFilters);
  const structured = filters.structured_filters;
  const lines: FilterLine[] = [];

  /**
   * A legal form as its name, falling back to whatever was stored.
   *
   * The fallback matters: the assistant may set these fields with the register's
   * word rather than its number — "Aktiengesellschaft" — and looking that up as
   * a key would find nothing. Printing it back unchanged is right, because it is
   * already a name.
   */
  const legalFormName = (form: string) => t(`filters.legalForm.name.${form}`, { default: form });

  const push = (label: string, value: string | number | undefined | null) => {
    if (value === undefined || value === null || value === '') return;
    lines.push({ label, value: String(value) });
  };

  const industries = Array.from(get(selectedIndustryCentroids).values());
  if (industries.length > 0) push(t('assistant.state.industries'), industries.join(', '));

  if (filters.contact_roles.length > 0) {
    // Keys in the store, labels on the screen — "technology" is not what
    // the customer chose, "Leiter/in Technik" is.
    push(
      t('filterLabels.contactRole'),
      filters.contact_roles.map((r) => getRoleLabel(r, t)).join(' → ')
    );
  }

  if (filters.cantons.length > 0) push(t('filterLabels.canton'), filters.cantons.join(', '));
  if (filters.languages.length > 0) {
    push(
      t('filters.regionTypes.languages'),
      filters.languages.map((l) => l.toUpperCase()).join(', ')
    );
  }
  if (filters.postal_codes.length > 0) {
    push(t('filterLabels.postalCode'), filters.postal_codes.join(', '));
  }
  if (filters.postal_radius.center_postal_code && filters.postal_radius.radius_km > 0) {
    push(
      t('filterLabels.postalRadius'),
      `${filters.postal_radius.center_postal_code} / ${filters.postal_radius.radius_km} km`
    );
  }

  // A range with one open end is written with a dash on that side rather than
  // dropped: "from 50" and "50 to 200" are different selections, and a summary
  // that renders them the same is worse than none.
  const range = ([from, to]: [number | null, number | null]) =>
    from || to ? `${from ?? '–'}–${to ?? '–'}` : '';
  push(t('filterLabels.employees'), range(filters.company_size_range));
  push(t('filterLabels.founding'), range(filters.founding_year_range));

  if (structured.branch_office_count.min > 0) {
    push(t('filterLabels.branches'), `≥ ${structured.branch_office_count.min}`);
  }
  if (structured.legal_form.length > 0) {
    push(t('data.legalForm'), structured.legal_form.map(legalFormName).join(', '));
  }
  if (structured.exclude_legal_form?.length) {
    push(
      t('filters.legalForm.excluded'),
      structured.exclude_legal_form.map(legalFormName).join(', ')
    );
  }
  if (structured.capital !== null) push(t('data.capital'), `≥ ${structured.capital}`);
  if (structured.revision_company) push(t('filterLabels.auditedBy'), structured.revision_company);
  if (structured.has_revisionsverzicht) push(t('data.auditExemption'), t('assistant.state.yes'));

  // Its own line rather than joining the channel list below. A customer who
  // ticked this said something about the companies, not about how to reach
  // them, and folding it into "Kanäle: Website, E-Mail, Lehrbetrieb" would read
  // as a fourth way of contacting somebody.
  if (structured.offers_apprenticeships) {
    push(t('filters.apprenticeships.label'), t('assistant.state.yes'));
  }

  // Its own line too, and for the same reason: it says something about the
  // companies rather than about how to reach them.
  if (structured.is_hiring) {
    push(t('filters.hiring.label'), t('assistant.state.yes'));
  }

  // Counted rather than named, because this file cannot name them.
  //
  // The labels live in the server-held catalogue, which loads asynchronously
  // and may not have arrived — this function is synchronous and is called from
  // places that have no business waiting on a fetch. A count is honest and
  // always available; a list that silently omitted the tools whose labels had
  // not loaded would be worse than no list.
  //
  // Two lines rather than one, because the two say opposite things: the first
  // is what a company uses, the second what its mail is missing.
  const technologies = structured.technologies ?? [];
  if (technologies.length > 0) {
    push(t('filters.technologies.title'), String(technologies.length));
  }

  const gaps = structured.mail_gaps ?? [];
  if (gaps.length > 0) {
    push(t('filters.technologies.gapsTitle'), String(gaps.length));
  }

  const fleet = structured.fleet;
  if (fleet && fleet.kinds.length > 0) {
    const kinds = fleet.kinds.map((kind) => t(`filters.fleet.${kind}`)).join(', ');
    push(
      t('filters.fleet.title'),
      fleet.min_vehicles ? `${kinds} · ≥ ${fleet.min_vehicles}` : kinds
    );
  }

  const roles = structured.group_roles ?? [];
  if (roles.length > 0) {
    push(t('filters.group.title'), roles.map((role) => t(`filters.group.${role}`)).join(', '));
  }

  const sales = structured.sales_models ?? [];
  if (sales.length > 0) {
    push(t('filters.salesModel.title'), sales.map((m) => t(`filters.salesModel.${m}`)).join(', '));
  }

  const certs = structured.certifications ?? [];
  if (certs.length > 0) {
    push(t('filters.certifications.title'), String(certs.length));
  }

  // The channels are one line, not eight. Each is a boolean, and a customer who
  // ticked three of them said one thing about reachability, not three things.
  const channels: [boolean, string][] = [
    [structured.has_website, t('data.website')],
    [structured.has_email, t('data.email')],
    [structured.has_phone, t('data.phone')],
    [structured.has_linkedin, t('data.linkedin')],
    [structured.has_facebook, t('data.facebook')],
    [structured.has_instagram, t('data.instagram')],
    [structured.has_twitter, t('data.twitter')]
  ];
  const wanted = channels.filter(([on]) => on).map(([, name]) => name);
  if (wanted.length > 0) push(t('assistant.state.channels'), wanted.join(', '));

  if (filters.text_constraints.length > 0) {
    push(t('data.text'), filters.text_constraints.join(', '));
  }
  if (filters.embedding_query) push(t('assistant.state.description'), filters.embedding_query);

  const signals = filters.signal_filters?.selected ?? [];
  if (signals.length > 0) {
    push(t('filters.signals.title'), String(signals.length));
  }
  if (filters.signal_filters?.capital_raised) {
    push(t('filters.signals.types.capital_raised'), t('assistant.state.yes'));
  }

  return lines;
}
